// @flow
import type { ContextRouter } from "react-router";
import React from "react";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import { Form, Field } from "react-final-form";
import styled, { css } from "styled-components";

import { NotFound as NotFoundPage } from "@nested/brand/src/pages/NotFound";
import { getConfig } from "@nested/config";
import { Sentry } from "@nested/isomorphic-sentry";
import { createAnalyticsAction } from "@nested/analytics";
import { getImage, request } from "@nested/utils";
import {
  TextInput as OriginalTextInput,
  media,
  mediumParagraph,
  sectionHeading,
  regularHeading,
  AddressPicker,
} from "@nested/brand";
import { Button } from "@nested/component-library";
import { fetchPropertyListings } from "../IndividualPropertyPage/ducks";

const { ENQUIRY_ENDPOINT } = getConfig();

const P = styled.p`
  ${mediumParagraph}
  display: block;
`;

const Breadcrumbs = styled.span`
  ${sectionHeading}
  text-align: left;
  margin-bottom: 0;
  font-size: 12px;
  line-height: 17px;
  vertical-align: top;
`;

const Heading = styled.h2`
  ${regularHeading}
  display: block;
`;

const TextInput = styled(OriginalTextInput)`
  margin-top: 10px;
`;

const PageWrapper = styled.div`
  box-sizing: border-box;
  padding: 10px;
  position: relative;
  max-width: 1920px;
  width: 100%;
  margin: 0 auto;
  background-color: white;
`;

const FormWrapper = styled.section`
  background-color: transparent;
  max-width: 420px;
  min-height: 100vh;
  position: relative;
  margin-bottom: 30px;

  ${media.tablet`
    margin: 30px auto 0 auto;
    max-width: 470px;
  `};
`;

const BackToListing = styled.section`
  align-items: center;
  display: flex;
  height: 72px;
  max-width: 1200px;
  margin: 0 auto;

  ${media.tablet`
    height: 110px;
  `};

  a {
    text-decoration: none;
  }
`;

const backToListingArrowStyle = css`
  width: 16px;
  height: 16px;
`;

const FormTitle = styled.div`
  margin: 0;
  text-align: left;
  padding-right: 10px;
  display: block;
  flex-grow: 1;
`;

const FormHeading = styled.div`
  display: flex;
  margin-bottom: 20px;
  align-items: center;
  justify-content: space-between;
`;

const Img = styled.img`
  height: 100px;
  width: 100px;
  object-fit: cover;
  margin: 5px 0;
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  padding-bottom: 10px;
`;

const SubmitButton = styled(Button)`
  margin-top: 50px;

  ${media.tablet`
    max-width: 215px;
  `};
`;

const ErrorMessage = styled(P)`
  color: ${({ theme }) => theme.palette.terracotta100};
  text-align: left;
  margin-bottom: 0;
`;

export const BackToListingBreadcrumb = ({ pathname }: { pathname: string }) => {
  return (
    <BackToListing>
      <Link to={pathname}>
        <img
          src={getImage("icons/arrowLeft.svg")}
          css={backToListingArrowStyle}
          alt="Left arrow"
        />
        <Breadcrumbs>Back to listing</Breadcrumbs>
      </Link>
    </BackToListing>
  );
};

export const EnquiryFormHeading = ({
  listingConfig: { address, externalId, images },
}: {
  listingConfig: {
    address: PropertyAddress,
    externalId: ID,
    images: Array<string>,
  },
}) => (
  <FormHeading>
    <FormTitle>
      <Heading>Enquiring about</Heading>
      <P data-test="property-enquiry-form-title">
        {`${address.street}, ${address.borough}, ${address.outcode}`}
      </P>
    </FormTitle>
    <Img
      src={getImage(`property-listings/${externalId}/${images[0]}`)}
      alt="Property image"
    />
  </FormHeading>
);

function ErrorComponent({ dirty, error, invalid, touched }) {
  return dirty && invalid && touched ? (
    <ErrorMessage>{error}</ErrorMessage>
  ) : null;
}

const required = (value) => (value ? undefined : "Required");
function validEmail(value: string = ""): void | string {
  const trimmedString = value.trim();
  /*
   * Match close.io email validator
   * see: https://github.com/closeio/cleancat/blob/71a1be23571bbca9685ca44527d8baa7b12cae32/cleancat/base.py#L168
   */
  const emailRegex = /^.+@[^.].*\.[a-z]{2,63}$/i;
  return emailRegex.test(trimmedString)
    ? undefined
    : "Please enter a valid email address.";
}
const validPhoneNumber = (value) => {
  /*
   * this allows only numbers and spaces, with an optional + at the beginning
   * the total length must be at least 8 in length
   */
  const phoneRegex = /^[+]{0,1}[0-9\s]{8,11}[0-9]+/;
  return phoneRegex.test(value)
    ? undefined
    : "Please enter a valid phone number.";
};

const maxLength = (max) => (value: string) =>
  value.length <= max
    ? undefined
    : `Length should be less than or equal to ${max}`;

const composeValidators =
  (...validators) =>
  (value: any) =>
    validators.reduce(
      (error, validator) => error || validator(value),
      undefined,
    );

type State = $ReadOnly<{
  propertyListing: {
    dealExternalId: "string",
    fetched: boolean,
    listingConfig: FullPropertyListingDatum,
  },
}>;

type OuterProps = {|
  ...ContextRouter<{ dealExternalId: string }>,
|};

type PropertyEnquiryProps = {
  ...OuterProps,
  listingConfig: {
    address: PropertyAddress,
    externalId: ID,
    images: Array<string>,
  },
  fetchedListingConfig: boolean,
  pushToGTM: ({
    name: string,
    email: string,
    phone: string,
    address: string,
    postcode: string,
    questions: string,
    deal_external_id: ID,
  }) => void,
};

type formState = {|
  name: string,
  email: string,
  phone: string,
  address: AddressPickerPostcodeDetailsLookup_postcodeDetails_addresses,
  questions: string,
|};

export class PropertyEnquiryComponent extends React.Component<PropertyEnquiryProps> {
  onSubmit = async (values: formState) => {
    const {
      listingConfig: { externalId } = {},
      history,
      pushToGTM,
    } = this.props;

    try {
      const initialValues = {
        deal_external_id: externalId,
      };

      const address = `${values.address.label}, ${values.address.postcode}`;

      const mergedValues = {
        ...initialValues,
        ...values,
        address,
      };

      await request(ENQUIRY_ENDPOINT, {
        body: JSON.stringify(mergedValues),
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
        // if having CORS problems in local dev:
        // mode: "no-cors",
      });

      pushToGTM({
        ...mergedValues,
        addressId: values.address?.id,
      });

      return history.push({
        pathname: `/enquiry-thank-you/${externalId}`,
      });
    } catch (error) {
      Sentry.captureException(error);
      return error;
    }
  };

  static fetchData = fetchPropertyListings;

  render() {
    const {
      match: {
        params: { dealExternalId },
      },
      listingConfig,
      fetchedListingConfig,
      location,
    } = this.props;

    if (fetchedListingConfig && !listingConfig) {
      // if the fetch fails, it must be an invalid ID
      Sentry.captureException(
        new Error(`No property listing found for ID: ${dealExternalId || ""}`),
      );
      return <NotFoundPage />;
    }

    if (!listingConfig) {
      return null;
    }

    return (
      <PageWrapper>
        <BackToListingBreadcrumb
          pathname={location.pathname.replace("/enquiry", "")}
        />
        <FormWrapper>
          <EnquiryFormHeading listingConfig={listingConfig} />
          <Form
            onSubmit={this.onSubmit}
            initialValues={{
              address: "",
              questions: "",
            }}
            render={({ handleSubmit, invalid, pristine, submitting }) => (
              <StyledForm onSubmit={handleSubmit}>
                <Field name="name" validate={required}>
                  {({ input, meta }) => (
                    <>
                      <ErrorComponent {...meta} />
                      <TextInput
                        {...input}
                        type="text"
                        placeholder="Your name *"
                        data-test="enquiry-form-name"
                      />
                    </>
                  )}
                </Field>
                <Field
                  name="email"
                  validate={composeValidators(required, validEmail)}
                >
                  {({ input, meta }) => (
                    <>
                      <ErrorComponent {...meta} />
                      <TextInput
                        hasErrors={!meta.pristine && meta.invalid}
                        {...input}
                        type="email"
                        placeholder="Your email address *"
                        data-test="enquiry-form-email"
                        className="fs-exclude"
                      />
                    </>
                  )}
                </Field>
                <Field
                  name="phone"
                  validate={composeValidators(
                    required,
                    maxLength(254),
                    validPhoneNumber,
                  )}
                >
                  {({ input, meta }) => (
                    <>
                      <ErrorComponent {...meta} />
                      <TextInput
                        {...input}
                        type="phone"
                        hasErrors={!meta.pristine && meta.invalid}
                        placeholder="Your phone number *"
                        data-test="enquiry-form-phone"
                        className="fs-exclude"
                      />
                    </>
                  )}
                </Field>

                <Field name="address">
                  {({ input }) => (
                    <AddressPicker
                      css={css`
                        margin-top: 10px;
                      `}
                      dataTest="property-enquiry-address-picker"
                      placeholder="Enter postcode"
                      includeBlacklisted
                      includeNotInServicedArea
                      {...input}
                    />
                  )}
                </Field>

                <Field name="questions">
                  {({ input }) => (
                    <TextInput
                      {...input}
                      placeholder="Got any questions?"
                      data-test="enquiry-form-questions"
                    />
                  )}
                </Field>

                <SubmitButton
                  data-test="property-enquiry-submit"
                  type="accent"
                  size="large"
                  disabled={pristine || invalid || submitting}
                >
                  Request details
                </SubmitButton>
              </StyledForm>
            )}
          />
        </FormWrapper>
      </PageWrapper>
    );
  }
}

export const mapStateToProps = (state: State) => ({
  listingConfig: state.propertyListing.listingConfig,
  fetchedListingConfig: state.propertyListing.fetched,
});

function mapDispatchToProps(dispatch: *) {
  return {
    pushToGTM(userDetails) {
      const analyticsAction = createAnalyticsAction({
        event: "propertyListingBuyerEnquiry",
        ...userDetails,
      });

      dispatch(analyticsAction);
    },
  };
}

export const PropertyEnquiry = connect<
  PropertyEnquiryProps,
  OuterProps,
  _,
  _,
  _,
  _,
>(
  mapStateToProps,
  mapDispatchToProps,
)(PropertyEnquiryComponent);

export const UniversalRender = PropertyEnquiry;
